
import React, { useMemo, useState } from 'react';
import { format, differenceInDays, startOfDay } from 'date-fns';
import { Card, CardContent } from "@/components/ui/card";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Task {
  id: string;
  content: string;
  start_date?: string;
  due_date?: string;
  labels?: { type: 'urgent' | 'critical' | 'normal' | 'low' }[];
  status?: string;
  start?: Date;
  end?: Date;
  priority?: string;
}

interface Column {
  id: string;
  title: string;
  tasks: Task[];
}

interface GanttViewProps {
  columns: { [key: string]: Column };
}

const GanttView = ({ columns }: GanttViewProps) => {
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Extract tasks with valid dates
  const tasks = useMemo(() => {
    console.log('GanttView - Raw columns data:', columns);
    
    const allTasks = Object.values(columns)
      .flatMap(column => {
        console.log('GanttView - Processing column:', column.title);
        return column.tasks
          .filter(task => {
            console.log('GanttView - Task dates:', {
              taskId: task.id,
              start_date: task.start_date,
              due_date: task.due_date,
              status: task.status
            });
            return task.start_date && task.due_date;
          })
          .map(task => ({
            id: task.id,
            name: task.content,
            start: task.start_date ? new Date(task.start_date) : null,
            end: task.due_date ? new Date(task.due_date) : null,
            priority: task.labels?.[0]?.type || 'normal',
            status: task.status,
            content: task.content
          }));
      })
      .filter(task => task.start && task.end);

    console.log('GanttView - Processed tasks with dates:', allTasks);
    return allTasks;
  }, [columns]);

  // If no tasks with dates, show message
  if (tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-[500px] text-slate-500 dark:text-slate-400">
        No tasks with dates found
      </div>
    );
  }

  // Calculate date range
  const minDate = startOfDay(new Date(Math.min(...tasks.map(task => task.start!.getTime()))));
  const maxDate = startOfDay(new Date(Math.max(...tasks.map(task => task.end!.getTime()))));
  
  // Generate array of dates for the timeline
  const dates: Date[] = [];
  let currentDate = minDate;
  while (currentDate <= maxDate) {
    dates.push(currentDate);
    currentDate = new Date(currentDate);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Priority colors mapping
  const priorityColors = {
    urgent: 'bg-red-500',
    critical: 'bg-orange-500',
    normal: 'bg-blue-500',
    low: 'bg-green-500'
  };

  // Calculate task position and width
  const getTaskStyle = (task: typeof tasks[0]) => {
    if (!task.start || !task.end) return { left: '0%', width: '0%' };
    
    const totalDays = differenceInDays(maxDate, minDate) + 1;
    const startDiff = differenceInDays(startOfDay(task.start), minDate);
    const duration = differenceInDays(startOfDay(task.end), startOfDay(task.start)) + 1;
    
    const left = `${(startDiff / totalDays) * 100}%`;
    const width = `${(duration / totalDays) * 100}%`;
    
    return { left, width };
  };

  return (
    <div className="w-full overflow-x-auto relative">
      <div className="min-w-[800px] p-4">
        {/* Timeline header */}
        <div className="flex border-b border-slate-200 dark:border-slate-700">
          <div className="w-48 flex-shrink-0" /> {/* Task names column */}
          <div className="flex-1 flex">
            {dates.map(date => (
              <div 
                key={date.toISOString()} 
                className="flex-1 px-1 text-center text-xs text-slate-600 dark:text-slate-400"
              >
                {format(date, 'MMM d')}
              </div>
            ))}
          </div>
        </div>

        {/* Tasks */}
        <div className="mt-4">
          {tasks.map(task => (
            <div key={task.id} className="flex mb-4 relative group">
              <div className="w-48 flex-shrink-0 pr-4 truncate text-sm text-slate-700 dark:text-slate-300">
                {task.name}
              </div>
              <div className="flex-1 relative h-8">
                {/* Background grid */}
                <div className="absolute inset-0 flex">
                  {dates.map(date => (
                    <div 
                      key={date.toISOString()} 
                      className="flex-1 border-r border-slate-200 dark:border-slate-700 last:border-r-0"
                    />
                  ))}
                </div>
                {/* Task bar */}
                <div
                  onClick={() => setSelectedTask(task)}
                  className={`absolute h-6 top-1 rounded ${task.status === 'done' ? 'bg-green-500 line-through opacity-70' : priorityColors[task.priority as keyof typeof priorityColors]} 
                    transition-opacity group-hover:opacity-80 cursor-pointer`}
                  style={getTaskStyle(task)}
                >
                  <div className="px-2 text-xs text-white truncate">
                    {task.name}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Task Details Popup */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-[90%] max-w-md m-4 relative">
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute right-2 top-2"
              onClick={() => setSelectedTask(null)}
            >
              <X className="h-4 w-4" />
            </Button>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-1">Task</h3>
                  <p className="text-sm">{selectedTask.content}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-sm mb-1">Start Date</h4>
                    <p className="text-sm">{format(selectedTask.start!, 'PP')}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm mb-1">Due Date</h4>
                    <p className="text-sm">{format(selectedTask.end!, 'PP')}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-sm mb-1">Priority</h4>
                  <p className="text-sm capitalize">{selectedTask.priority}</p>
                </div>
                {selectedTask.status && (
                  <div>
                    <h4 className="font-medium text-sm mb-1">Status</h4>
                    <p className="text-sm capitalize">{selectedTask.status}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GanttView;

